/**
 * Pure maths for Home's money and count tiles. Integer minor units throughout; nothing here rounds money.
 * Percentages are the only rounded figure and they are display-only.
 */
import type { BillSummary, CheckinRow, Collection } from "../../api/lists";
import { minutesOfDay } from "../../lib/dates";

export const sumMinor = (xs: readonly number[]) => xs.reduce((a, b) => a + b, 0);
/** The sum, or null if any figure was withheld by the server (a D57 field lever is off): never a partial total. */
export const sumKnown = (xs: readonly (number | null)[]): number | null => (xs.some((x) => x === null) ? null : sumMinor(xs as number[]));
/** Still owing. A bill whose balance was withheld counts if the list returned it as unpaid or part-paid. */
export const owes = (b: Pick<BillSummary, "balance" | "paymentStatus">) => (b.balance === null ? b.paymentStatus === "unpaid" || b.paymentStatus === "partial" : b.balance > 0);

/**
 * Money that came in. `GET /home/collections` applies the `GET /reports/collections` rule on the server (captured, less
 * wallet tender, which was counted when it first came in; gross of refunds), so every entry counts.
 */

/** Rows from the start of their business day up to `minute` on the wall clock: "at the same time" for last week. */
export const upToMinute = <T>(rows: readonly T[], at: (row: T) => number, timeZone: string, minute: number) => rows.filter((r) => minutesOfDay(at(r), timeZone) <= minute);

export const totalCollected = (ps: readonly Collection[]) => sumMinor(ps.map((p) => p.amount));

export function byMethod(ps: readonly Collection[]): Array<{ method: string; amount: number }> {
  const m = new Map<string, number>();
  for (const p of ps) m.set(p.method, (m.get(p.method) ?? 0) + p.amount);
  return [...m.entries()].map(([method, amount]) => ({ method, amount })).sort((a, b) => b.amount - a.amount || a.method.localeCompare(b.method));
}

export const OPEN_HOUR = 5;
export const CLOSE_HOUR = 21;

/** Collections per hour of the business day (`hours` are local hours). Hours with no payments are 0, not missing. */
export function hourlyCollections(ps: readonly Collection[], timeZone: string, minHour = OPEN_HOUR, maxHour = CLOSE_HOUR): { hours: number[]; values: number[] } {
  const per = new Map<number, number>();
  for (const p of ps) {
    const h = Math.floor(minutesOfDay(p.createdAt, timeZone) / 60);
    per.set(h, (per.get(h) ?? 0) + p.amount);
  }
  const hs = [...per.keys()];
  const lo = Math.min(minHour, ...hs);
  const hi = Math.max(maxHour, ...hs);
  const hours = Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
  return { hours, values: hours.map((h) => per.get(h) ?? 0) };
}

/** Whole-number percent change, or null when there is nothing to compare with. */
export function percentChange(cur: number, prev: number): number | null {
  if (prev === 0) return null;
  return Math.round(((cur - prev) * 100) / prev);
}

export type Tone = "good" | "bad" | "flat";
export function deltaTone(pct: number | null, higherIsBetter = true): Tone {
  if (pct === null || pct === 0) return "flat";
  return (pct > 0) === higherIsBetter ? "good" : "bad";
}

export const countBills = (bs: readonly BillSummary[]) => bs.filter((b) => b.status !== "void" && b.voidedAt === null).length;

export const countVisits = (cs: readonly CheckinRow[]) => cs.filter((c) => c.decision === "allow" && c.countedAsVisit).length;

/** A bill summary or a `GET /dues` row (which is never void and has no paymentStatus). */
export type DueLike = Pick<BillSummary, "balance" | "dueDate"> & { voidedAt?: number | null; paymentStatus?: BillSummary["paymentStatus"] };

/** `total` and `overdueTotal` are null when the server withheld a balance. */
export interface DuesSummary { count: number; total: number | null; overdueCount: number; overdueTotal: number | null }

/** Bills still owing money. A bill is overdue when its due date is before `today` (string compare; no date maths). */
export function summariseDues(bills: readonly DueLike[], today: string): DuesSummary {
  const owing = bills.filter((b) => (b.voidedAt ?? null) === null && (b.balance === null ? b.paymentStatus === "unpaid" || b.paymentStatus === "partial" : b.balance > 0));
  const overdue = owing.filter((b) => b.dueDate !== null && b.dueDate < today);
  return { count: owing.length, total: sumKnown(owing.map((b) => b.balance)), overdueCount: overdue.length, overdueTotal: sumKnown(overdue.map((b) => b.balance)) };
}

/** A "nice" axis step in minor units (1, 2, 2.5 or 5 × 10^k) so that `max / step <= lines`. */
export function niceStep(maxMinor: number, lines = 4): number {
  if (maxMinor <= 0) return 100;
  for (let pow = 1; ; pow *= 10) {
    for (const m of [1, 2, 2.5, 5]) {
      const step = m * pow;
      if (Number.isInteger(step) && maxMinor <= step * lines) return step;
    }
  }
}

/** Share of a total as a percentage with one decimal, for bar widths only. */
export const sharePct = (part: number, whole: number) => (whole === 0 ? 0 : Math.round((part * 1000) / whole) / 10);
